import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmailTemplates, type EmailTemplate } from '@/hooks/useEmailTemplates';
import { EmailTemplatePreviewModal } from '@/components/admin/EmailTemplatePreviewModal';
import { SendTestEmailModal } from '@/components/admin/SendTestEmailModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Eye, Edit, Copy, History, Send } from 'lucide-react';

const AdminEmailTemplates = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { templates, isLoading, createNewVersion } = useEmailTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isTestEmailOpen, setIsTestEmailOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'draft'>('all');

  const handlePreview = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setTimeout(() => setSelectedTemplate(null), 300);
  };

  const handleCopyCode = (html: string) => {
    navigator.clipboard.writeText(html);
    toast({
      title: 'Código copiado',
      description: 'O código HTML foi copiado para a área de transferência',
    });
  };

  const handleDuplicate = async (templateId: string) => {
    await createNewVersion.mutateAsync(templateId);
  };

  const filteredTemplates = templates?.filter(t => {
    if (filter === 'active') return t.is_active;
    if (filter === 'draft') return t.is_draft;
    return true;
  }) || [];

  const getCategoryColor = (category: string) => {
    return category === 'authentication' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-purple-500/20 text-purple-400 border-purple-500/30';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Templates de E-mail</h1>
          <p className="text-white/60">
            Gerencie os templates de email da aplicação
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-luxury-pinkBright text-white' : 'bg-white/5 border-white/10 text-white'}
        >
          Todos
        </Button>
        <Button
          variant={filter === 'active' ? 'default' : 'outline'}
          onClick={() => setFilter('active')}
          className={filter === 'active' ? 'bg-luxury-pinkBright text-white' : 'bg-white/5 border-white/10 text-white'}
        >
          Ativos
        </Button>
        <Button
          variant={filter === 'draft' ? 'default' : 'outline'}
          onClick={() => setFilter('draft')}
          className={filter === 'draft' ? 'bg-luxury-pinkBright text-white' : 'bg-white/5 border-white/10 text-white'}
        >
          Rascunhos
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-white/60">Carregando templates...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredTemplates.map((template) => (
            <div key={template.id} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/[0.07] transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-white">{template.name}</h3>
                    {template.is_active && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">ATIVO</Badge>
                    )}
                    {template.is_draft && (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">DRAFT</Badge>
                    )}
                    <Badge variant="outline" className="bg-white/5 border-white/10 text-white/60">
                      v{template.version}
                    </Badge>
                  </div>
                  <p className="text-sm text-white/60 mb-3">{template.description}</p>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline" className={getCategoryColor(template.category)}>
                      {template.category === 'authentication' ? 'Autenticação' : 'Notificação'}
                    </Badge>
                    <Badge variant="outline" className="bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30">
                      {template.supabase_type}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs text-white/50 mb-2">Variáveis disponíveis:</p>
                <div className="flex flex-wrap gap-1">
                  {template.variables.map((variable: string) => (
                    <code key={variable} className="text-xs bg-white/5 px-2 py-1 rounded border border-white/10 text-white/70">
                      {variable}
                    </code>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePreview(template)}
                  className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedTemplate(template);
                    setIsTestEmailOpen(true);
                  }}
                  className="bg-luxury-pinkBright/20 border-luxury-pinkBright/30 hover:bg-luxury-pinkBright/30 text-luxury-pinkBright"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Testar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/main/joia/admin/email-templates/${template.id}`)}
                  className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyCode(template.html_content)}
                  className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDuplicate(template.id)}
                  className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                >
                  <History className="w-4 h-4 mr-2" />
                  Nova Versão
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Documentação das Variáveis */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">📚 Variáveis do Supabase Auth</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <code className="text-xs text-luxury-pinkBright">{'{{ .ConfirmationURL }}'}</code>
            <p className="text-xs text-white/60 mt-1">URL completa de confirmação</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <code className="text-xs text-luxury-pinkBright">{'{{ .Token }}'}</code>
            <p className="text-xs text-white/60 mt-1">Token em formato legível</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <code className="text-xs text-luxury-pinkBright">{'{{ .TokenHash }}'}</code>
            <p className="text-xs text-white/60 mt-1">Hash do token</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <code className="text-xs text-luxury-pinkBright">{'{{ .SiteURL }}'}</code>
            <p className="text-xs text-white/60 mt-1">URL base do site</p>
          </div>
        </div>
      </div>

      {/* Modals - Pass template directly without conversion */}
      {selectedTemplate && (
        <>
          <EmailTemplatePreviewModal
            template={selectedTemplate}
            isOpen={isPreviewOpen}
            onClose={handleClosePreview}
          />
          <SendTestEmailModal
            isOpen={isTestEmailOpen}
            onClose={() => {
              setIsTestEmailOpen(false);
              setTimeout(() => setSelectedTemplate(null), 300);
            }}
            template={{
              id: selectedTemplate.id,
              name: selectedTemplate.name,
              html_content: selectedTemplate.html_content,
              supabase_type: selectedTemplate.supabase_type,
            }}
          />
        </>
      )}
    </div>
  );
};

export default AdminEmailTemplates;
